import React from 'react';
import {
    Users,
    Wallet,
    ArrowLeftRight,
    TrendingUp,
    AlertCircle,
    ChevronRight,
    Zap,
    Briefcase,
    ShieldCheck,
    Building2,
    CheckCircle2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/shared/lib/utils';
import { useAdminData } from '@/shared/hooks/useAdminData';

const AdminDashboard: React.FC = () => {
    const { stats: adminStats, recentTransactions, pendingApplications, loading, approveApplication } = useAdminData();
    const navigate = useNavigate();

    const stats = [
        {
            label: '총 사용자',
            value: `${adminStats.totalUsers.toLocaleString()}명`,
            change: '전체',
            icon: Users,
            color: 'text-blue-500',
            bg: 'bg-blue-500/10'
        },
        {
            label: '총 보유 자산 (KRW)',
            value: `₩${(adminStats.totalBalance).toLocaleString()}`,
            change: '실시간',
            icon: Wallet,
            color: 'text-emerald-500',
            bg: 'bg-emerald-500/10'
        },
        {
            label: '미승인 KYC',
            value: `${adminStats.pendingKYC}건`,
            change: '대기중',
            icon: ShieldCheck,
            color: 'text-amber-500',
            bg: 'bg-amber-500/10'
        },
        {
            label: '미처리 입출금',
            value: `${adminStats.pendingWithdrawals}건`,
            change: '주의',
            icon: AlertCircle,
            color: 'text-rose-500',
            bg: 'bg-rose-500/10'
        },
    ];

    const handleApprove = async (appId: string, userId: string, type: string) => {
        const confirmApprove = window.confirm('해당 신청을 승인하시겠습니까?');
        if (confirmApprove) {
            const result = await approveApplication(appId, userId, type);
            if (result.success) {
                alert('정상적으로 승인되었습니다.');
            } else {
                alert('승인 처리 중 오류가 발생했습니다.');
            }
        }
    };

    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <div className="flex flex-col items-center gap-4">
                    <Zap className="h-12 w-12 text-blue-500 animate-pulse" />
                    <p className="text-[10px] font-black uppercase tracking-[0.4em] text-muted-foreground">관리자 데이터 로딩중...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-12 animate-in fade-in duration-1000">
            {/* Header Section */}
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-10">
                <div className="space-y-4">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-600 dark:text-blue-500 transition-colors">
                        <Zap className="h-3.5 w-3.5 fill-blue-500" />
                        <span className="text-[10px] font-black uppercase tracking-[0.2em]">시스템 관제 센터 (HQ)</span>
                    </div>
                    <h1 className="text-4xl md:text-5xl font-black tracking-tighter leading-[0.9] uppercase text-foreground">
                        관리자 대시보드,<br />
                        <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 dark:from-blue-500 dark:via-indigo-500 dark:to-purple-500 font-black">
                            리얼타임 시스템 인사이트
                        </span>
                    </h1>
                </div>

                <div className="flex items-center gap-4 bg-card border border-border p-2 rounded-[2rem] shadow-xl transition-colors">
                    <button className="px-8 h-12 rounded-2xl bg-blue-600 text-white text-[10px] font-black uppercase tracking-widest shadow-lg shadow-blue-600/20 hover:scale-[1.02] active:scale-95 transition-all">전체 보고서</button>
                    <button className="px-8 h-12 rounded-2xl bg-accent text-muted-foreground text-[10px] font-black uppercase tracking-widest hover:text-foreground transition-all">시스템 설정</button>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {stats.map((stat, i) => (
                    <div key={i} className="group p-8 rounded-[2.5rem] bg-card border border-border hover:border-blue-500/30 transition-all duration-500 shadow-xl hover:shadow-2xl">
                        <div className="flex items-start justify-between mb-6">
                            <div className={cn("p-4 rounded-2xl shadow-inner", stat.bg)}>
                                <stat.icon className={cn("h-6 w-6", stat.color)} />
                            </div>
                            <span className="text-[10px] font-black text-emerald-500 bg-emerald-500/10 px-2.5 py-1 rounded-full uppercase tracking-widest italic">{stat.change}</span>
                        </div>
                        <p className="text-[10px] font-black text-muted-foreground uppercase tracking-widest mb-1 italic">{stat.label}</p>
                        <h3 className="text-3xl font-black text-foreground tracking-tighter tabular-nums group-hover:text-blue-600 transition-colors uppercase">{stat.value}</h3>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Recent Activities */}
                <div className="lg:col-span-2 space-y-8">
                    <div className="rounded-[3rem] bg-card border border-border overflow-hidden shadow-2xl transition-colors">
                        <div className="p-10 border-b border-border flex items-center justify-between bg-accent/30">
                            <div className="space-y-1">
                                <h3 className="font-black flex items-center gap-3 text-foreground text-xl tracking-tighter uppercase">
                                    <ArrowLeftRight className="h-6 w-6 text-blue-600 dark:text-blue-500" /> 실시간 트랜잭션
                                </h3>
                                <p className="text-[9px] font-black text-muted-foreground uppercase tracking-[0.3em]">시스템 전역 거래 로그</p>
                            </div>
                            <button className="h-10 px-6 rounded-xl bg-accent text-[10px] font-black uppercase tracking-widest text-muted-foreground hover:text-foreground transition-all border border-border">전체 보기</button>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-left">
                                <thead className="bg-accent/50 text-[10px] uppercase font-black tracking-[0.2em] text-muted-foreground">
                                    <tr>
                                        <th className="px-10 py-6">사용자 / 시간</th>
                                        <th className="px-10 py-6">유형</th>
                                        <th className="px-10 py-6 text-right">금액</th>
                                        <th className="px-10 py-6 text-center">상태</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border">
                                    {recentTransactions.map((tx) => (
                                        <tr key={tx.id} className="group hover:bg-accent/50 transition-all duration-500">
                                            <td className="px-10 py-8">
                                                <p className="text-sm font-black text-foreground uppercase tracking-tight">{tx.userId}</p>
                                                <p className="text-[10px] font-bold text-muted-foreground mt-1 uppercase tracking-tighter italic">
                                                    {tx.timestamp?.toDate ? tx.timestamp.toDate().toLocaleString() : 'Just now'}
                                                </p>
                                            </td>
                                            <td className="px-10 py-8 text-xs font-black text-muted-foreground uppercase tracking-widest">
                                                <span className="px-3 py-1 bg-accent border border-border rounded-lg">{tx.type}</span>
                                            </td>
                                            <td className="px-10 py-8 text-right font-mono text-base font-black text-foreground tabular-nums tracking-tighter">
                                                {tx.amount?.toLocaleString()}
                                            </td>
                                            <td className="px-10 py-8">
                                                <div className="flex justify-center">
                                                    <span className={cn(
                                                        "px-4 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-[0.2em] border",
                                                        tx.status === 'COMPLETED' ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20' :
                                                            tx.status === 'WIN' ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20' :
                                                                tx.status === 'LOSS' ? 'bg-rose-500/10 text-rose-600 border-rose-500/20' :
                                                                    tx.status === 'PENDING' ? 'bg-amber-500/10 text-amber-600 border-amber-500/20' :
                                                                        'bg-red-500/10 text-red-600 border-red-500/20'
                                                    )}>
                                                        {tx.status}
                                                    </span>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {recentTransactions.length === 0 && (
                                        <tr>
                                            <td colSpan={4} className="px-10 py-20 text-center">
                                                <p className="text-[10px] font-black text-muted-foreground uppercase tracking-widest">거래 내역이 없습니다</p>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Notifications Area */}
                <div className="space-y-8">
                    <div className="rounded-[3rem] bg-card border border-border p-10 shadow-2xl space-y-8 transition-colors">
                        <div className="space-y-1">
                            <h3 className="font-black flex items-center gap-3 text-foreground text-xl tracking-tighter uppercase">
                                <AlertCircle className="h-6 w-6 text-rose-600" /> 승인 대기 리스트
                            </h3>
                            <p className="text-[9px] font-black text-muted-foreground uppercase tracking-[0.3em]">즉각적인 처리가 필요합니다</p>
                        </div>

                        <div className="space-y-4">
                            {pendingApplications.map((app) => (
                                <div key={app.id} className="group p-6 rounded-3xl bg-accent/30 border border-border hover:border-blue-500/30 transition-all duration-500">
                                    <div className="flex items-center gap-4 mb-4">
                                        <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center text-blue-600">
                                            {app.type === 'ID_CARD' ? <ShieldCheck className="h-5 w-5" /> : <Building2 className="h-5 w-5" />}
                                        </div>
                                        <div>
                                            <p className="text-xs font-black text-foreground uppercase tracking-tight">
                                                {app.type === 'ID_CARD' ? '본인인증(KYC) 신청' : '계좌인증 신청'}
                                            </p>
                                            <p className="text-[9px] font-bold text-muted-foreground uppercase italic tracking-tighter">{app.userId}</p>
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => handleApprove(app.id, app.userId, app.type)}
                                            className="flex-1 h-10 rounded-xl bg-blue-600 text-white text-[9px] font-black uppercase tracking-widest hover:bg-blue-500 transition-all"
                                        >
                                            승인하기
                                        </button>
                                        <button className="h-10 px-4 rounded-xl bg-accent text-rose-500 text-[9px] font-black uppercase tracking-widest hover:bg-rose-500/10 transition-all">거절</button>
                                    </div>
                                </div>
                            ))}
                            {pendingApplications.length === 0 && (
                                <div className="p-8 text-center space-y-3 border-2 border-dashed border-border rounded-3xl">
                                    <CheckCircle2 className="h-8 w-8 text-emerald-500/20 mx-auto" />
                                    <p className="text-[10px] font-black text-muted-foreground uppercase tracking-widest italic opacity-40">처리 대기 중인 신청이 없습니다</p>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="rounded-[3rem] bg-card border border-border p-10 shadow-2xl space-y-8 transition-colors">
                        <div className="space-y-1">
                            <h3 className="font-black flex items-center gap-3 text-foreground text-xl tracking-tighter uppercase">
                                <TrendingUp className="h-6 w-6 text-indigo-600" /> 시스템 안정성
                            </h3>
                            <p className="text-[9px] font-black text-muted-foreground uppercase tracking-[0.3em]">시스템 인프라 전역 감시 중</p>
                        </div>
                        <div className="space-y-4">
                            <div className="h-2 w-full bg-accent rounded-full overflow-hidden">
                                <div className="h-full w-full bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full animate-pulse"></div>
                            </div>
                            <div className="flex justify-between text-[10px] font-black text-muted-foreground uppercase">
                                <span>보안 상태</span>
                                <span className="text-indigo-600 italic">보호됨</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;
